import React, { useState, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Truck, Search, ScanLine, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

let _k = 0;
const nextKey = () => `l${Date.now()}_${_k++}`;

export default function Purchase() {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const fileRef = useRef();
  const [vendorId, setVendorId] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [lines, setLines] = useState([]);
  const [prodQuery, setProdQuery] = useState("");
  const [paid, setPaid] = useState("");
  const [method, setMethod] = useState("cash");
  const [saving, setSaving] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [review, setReview] = useState(null);

  const { data: vendors } = useQuery({ queryKey: ["vendors-list"], queryFn: async () => (await api.get("/vendors")).data });
  const { data: productData } = useQuery({ queryKey: ["purchase-products"], queryFn: async () => (await api.get("/products", { params: { limit: 500 } })).data });
  const products = productData?.items || [];

  const filtered = useMemo(() => {
    if (!prodQuery) return [];
    const q = prodQuery.toLowerCase();
    return products.filter((p) => p.name.toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q)).slice(0, 6);
  }, [prodQuery, products]);

  function addLine(p) {
    if (lines.find((l) => l.product_id === p.id)) return;
    setLines([...lines, { key: nextKey(), product_id: p.id, name: p.name, qty: 1, purchase_price: p.purchase_price, tax_rate: p.tax_rate }]);
    setProdQuery("");
  }
  function updateLine(key, f, v) { setLines((ls) => ls.map((l) => l.key === key ? { ...l, [f]: v } : l)); }
  function removeLine(key) { setLines((ls) => ls.filter((l) => l.key !== key)); }

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    lines.forEach((l) => { const line = l.qty * Number(l.purchase_price); sub += line; tax += line * l.tax_rate / 100; });
    return { sub, tax, grand: sub + tax };
  }, [lines]);

  async function onOcrFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ocr/parse-bill", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReview({ ...data.parsed, preview: data.preview });
      toast.success("Bill scanned · review before saving");
    } catch (err) { toast.error(apiError(err)); } finally { setOcrLoading(false); }
  }

  function applyReview() {
    if (review.invoice_no) setInvoiceNo(review.invoice_no);
    const v = (vendors || []).find((x) => (x.name || "").toLowerCase() === (review.vendor_name || "").toLowerCase());
    if (v) setVendorId(v.id);
    const newLines = (review.items || []).map((it) => ({
      key: nextKey(), product_id: null, name: it.name || "Item",
      qty: Number(it.qty) || 1, purchase_price: Number(it.rate) || 0, tax_rate: Number(it.tax_rate) || 0,
    }));
    setLines((ls) => [...ls, ...newLines]);
    setReview(null);
    toast.success(`${newLines.length} item(s) imported — verify quantities & rates`);
  }
  function updReviewItem(i, k, v) { setReview((r) => ({ ...r, items: r.items.map((it, x) => x === i ? { ...it, [k]: v } : it) })); }
  function rmReviewItem(i) { setReview((r) => ({ ...r, items: r.items.filter((_, x) => x !== i) })); }

  async function submit() {
    if (!lines.length) { toast.error("Add at least one item"); return; }
    setSaving(true);
    try {
      await api.post("/purchases", {
        vendor_id: vendorId || null, vendor_name: (vendors || []).find((v) => v.id === vendorId)?.name || (review?.vendor_name || ""),
        invoice_no: invoiceNo,
        items: lines.map((l) => ({ product_id: l.product_id || null, name: l.name, qty: Number(l.qty), purchase_price: Number(l.purchase_price), tax_rate: Number(l.tax_rate) })),
        paid_amount: Number(paid || 0), payment_method: method,
      });
      toast.success("Purchase recorded · Stock updated");
      navigate("/purchase-history");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h2 className="font-display text-2xl font-bold text-[#0F172A]">New Purchase</h2>
          <p className="text-slate-500 text-sm">Record stock purchase from a vendor</p></div>
        {hasPerm("ocr_import") && (
          <>
            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.pdf,image/*,application/pdf" className="hidden" onChange={onOcrFile} data-testid="ocr-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={ocrLoading} variant="outline" className="border-[#1A2A5E] text-[#1A2A5E]" data-testid="ocr-upload-btn">
              {ocrLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Scanning...</> : <><ScanLine className="h-4 w-4 mr-2" />Upload Bill (OCR)</>}
            </Button>
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-5">
        <Card className="p-5 border-slate-200 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Vendor</Label>
              <Select value={vendorId} onValueChange={setVendorId}>
                <SelectTrigger className="mt-1" data-testid="purchase-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent className="bg-white/95 backdrop-blur-md">{(vendors || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Invoice No</Label><Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} className="mt-1" data-testid="purchase-invoice" /></div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={prodQuery} onChange={(e) => setProdQuery(e.target.value)} placeholder="Search product to add..." className="pl-9" data-testid="purchase-product-search" />
            {filtered.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white/95 backdrop-blur-md rounded-lg border border-slate-200 shadow-lg overflow-hidden">
                {filtered.map((p) => (
                  <button key={p.id} onClick={() => addLine(p)} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm flex justify-between">
                    <span>{p.name}</span><span className="text-slate-400 font-mono">{money(p.purchase_price)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            {lines.map((l) => (
              <div key={l.key} className="grid grid-cols-[1fr_70px_90px_70px_36px] gap-2 items-center bg-slate-50 rounded-lg p-2">
                <Input value={l.name} onChange={(e) => updateLine(l.key, "name", e.target.value)} className="h-8 text-sm" />
                <Input type="number" value={l.qty} onChange={(e) => updateLine(l.key, "qty", e.target.value)} className="h-8 text-center font-mono" />
                <Input type="number" value={l.purchase_price} onChange={(e) => updateLine(l.key, "purchase_price", e.target.value)} className="h-8 text-right font-mono" />
                <Input type="number" value={l.tax_rate} onChange={(e) => updateLine(l.key, "tax_rate", e.target.value)} className="h-8 text-center font-mono" />
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeLine(l.key)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            ))}
            {!lines.length && <div className="text-center py-10 text-slate-400 text-sm"><Truck className="h-8 w-8 mx-auto mb-2 opacity-40" />Search & add products, or Upload a Bill (OCR)</div>}
            {lines.length > 0 && <div className="grid grid-cols-[1fr_70px_90px_70px_36px] gap-2 text-[10px] text-slate-400 px-2 uppercase tracking-wide"><span>Item</span><span className="text-center">Qty</span><span className="text-right">Cost</span><span className="text-center">GST%</span><span /></div>}
          </div>
        </Card>

        <Card className="p-5 border-slate-200 h-fit space-y-3 lg:sticky lg:top-24">
          <h3 className="font-display font-semibold text-[#1A2A5E]">Summary</h3>
          <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-mono">{money(totals.sub)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-slate-500">Tax</span><span className="font-mono">{money(totals.tax)}</span></div>
          <div className="flex justify-between font-bold text-[#1A2A5E] pt-2 border-t"><span>Grand Total</span><span className="font-mono text-lg">{money(totals.grand)}</span></div>
          <div><Label>Payment Method</Label>
            <Select value={method} onValueChange={setMethod}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white/95 backdrop-blur-md">{["cash", "upi", "card", "credit"].map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Paid Amount</Label><Input type="number" value={paid} onChange={(e) => setPaid(e.target.value)} placeholder={totals.grand.toFixed(2)} className="mt-1 font-mono" data-testid="purchase-paid" /></div>
          <div className="flex justify-between text-sm"><span className="text-slate-500">Outstanding</span><span className="font-mono text-rose-600">{money(Math.max(0, totals.grand - Number(paid || 0)))}</span></div>
          <Button className="w-full bg-[#F4571E] hover:bg-[#D9420D]" disabled={saving || !lines.length} onClick={submit} data-testid="save-purchase">{saving ? "Saving..." : "Save Purchase"}</Button>
        </Card>
      </div>

      <Dialog open={!!review} onOpenChange={() => setReview(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#1A2A5E] flex items-center gap-2"><ScanLine className="h-5 w-5" />Review Imported Bill</DialogTitle></DialogHeader>
          {review && (
            <div className="space-y-4" data-testid="ocr-review">
              <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">AI-extracted data — please verify every value before importing. Nothing is saved until you confirm.</p>
              {review.preview && <img src={review.preview} alt="bill" className="max-h-40 rounded border mx-auto" />}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Vendor</Label><Input value={review.vendor_name || ""} onChange={(e) => setReview({ ...review, vendor_name: e.target.value })} className="mt-1" /></div>
                <div><Label>Invoice No</Label><Input value={review.invoice_no || ""} onChange={(e) => setReview({ ...review, invoice_no: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_60px_80px_60px_32px] gap-2 text-[10px] text-slate-400 uppercase px-1"><span>Item</span><span className="text-center">Qty</span><span className="text-right">Rate</span><span className="text-center">GST%</span><span /></div>
                {(review.items || []).map((it, i) => (
                  <div key={i} className="grid grid-cols-[1fr_60px_80px_60px_32px] gap-2 items-center">
                    <Input value={it.name || ""} onChange={(e) => updReviewItem(i, "name", e.target.value)} className="h-8 text-sm" />
                    <Input type="number" value={it.qty || 0} onChange={(e) => updReviewItem(i, "qty", e.target.value)} className="h-8 text-center" />
                    <Input type="number" value={it.rate || 0} onChange={(e) => updReviewItem(i, "rate", e.target.value)} className="h-8 text-right" />
                    <Input type="number" value={it.tax_rate || 0} onChange={(e) => updReviewItem(i, "tax_rate", e.target.value)} className="h-8 text-center" />
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => rmReviewItem(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                {!(review.items || []).length && <p className="text-sm text-slate-400 text-center py-3">No line items detected. Add them manually after importing.</p>}
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setReview(null)}>Cancel</Button>
                <Button className="bg-[#1A2A5E]" onClick={applyReview} data-testid="ocr-import-confirm">Import to Purchase</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
